import { useEffect, useState } from "react";
import axios from "axios";
import {
    Alert,
    Box,
    Button,
    ButtonBase,
    Card,
    CardContent,
    CircularProgress,
    FormControlLabel,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import axiosInstance from "../AxiosInstance";

interface Connection {
    isEnabled: boolean;
    credentialsConfigured: boolean;
}

interface EposSettingsPageProps {
    embedded?: boolean;
}

const messageFromError = (error: unknown, fallback: string) => {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data as { message?: string; detail?: string } | undefined;
        return data?.message ?? data?.detail ?? fallback;
    }
    return fallback;
};

export default function EposSettingsPage({ embedded = false }: EposSettingsPageProps) {
    const [connection, setConnection] = useState<Connection | null>(null);
    const [selectedRetailer, setSelectedRetailer] = useState("sainsburys");
    const [enabled, setEnabled] = useState(false);
    const [userId, setUserId] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const load = async () => {
        setLoading(true);
        setError("");
        try {
            const response = await axiosInstance.get<Connection>("/epos/sainsburys");
            setConnection(response.data);
            setEnabled(response.data.isEnabled);
        } catch (requestError) {
            setError(messageFromError(requestError, "EPOS settings could not be loaded."));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void load();
    }, []);

    const save = async () => {
        if ((userId.length > 0) !== (password.length > 0)) {
            setError("Enter both your Sainsbury's user ID and password.");
            return;
        }

        setSaving(true);
        setError("");
        setSuccess("");
        try {
            const response = await axiosInstance.put<Connection>("/epos/sainsburys", {
                isEnabled: enabled,
                userId: userId || null,
                password: password || null
            });
            setConnection(response.data);
            setUserId("");
            setPassword("");
            setSuccess("Sainsbury's settings saved.");
        } catch (requestError) {
            setError(messageFromError(requestError, "EPOS settings could not be saved."));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress /></Box>;
    }

    return (
        <Box sx={{ maxWidth: embedded ? "none" : 960, mx: "auto", py: embedded ? 0 : { xs: 2, md: 5 }, px: embedded ? 0 : { xs: 1, sm: 2 } }}>
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

            <Stack spacing={3}>
                {/* <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.75 }}>Choose a retailer</Typography>
                </Box> */}

                <Card
                    variant="outlined"
                    sx={{
                        borderColor: selectedRetailer === "sainsburys" ? "primary.main" : "divider",
                        borderWidth: selectedRetailer === "sainsburys" ? 2 : 1,
                        transition: "border-color 160ms ease, box-shadow 160ms ease",
                        boxShadow: selectedRetailer === "sainsburys" ? "0 8px 24px rgba(25, 118, 210, 0.10)" : "none"
                    }}
                >
                    <ButtonBase
                        component="div"
                        role="button"
                        aria-label="Select Sainsbury's"
                        aria-pressed={selectedRetailer === "sainsburys"}
                        onClick={() => setSelectedRetailer("sainsburys")}
                        sx={{ display: "block", width: "100%", textAlign: "left", borderRadius: 1 }}
                    >
                        <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
                            <Box sx={{ display: "grid", placeItems: "center", width: 52, height: 52, flexShrink: 0, borderRadius: 2, bgcolor: "#fff3e0", color: "#e65100" }}>
                                <StorefrontOutlinedIcon />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>Sainsbury&apos;s</Typography>
                            </Box>
                        </CardContent>
                    </ButtonBase>
                </Card>

                {selectedRetailer === "sainsburys" && (
                    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3.5 }, borderRadius: 2 }}>
                        <Stack spacing={3}>
                            <Box>
                                <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                                    Enter the details you use to sign in to Circana Unify+.
                                </Typography>
                            </Box>

                            {connection?.credentialsConfigured && (
                                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ p: 2, borderRadius: 1.5, bgcolor: "action.hover" }}>
                                    <LockOutlinedIcon color="action" fontSize="small" sx={{ mt: 0.25 }} />
                                    <Typography variant="body2" color="text.secondary">
                                        {connection?.credentialsConfigured
                                            ? "Your details are already saved. Leave both fields blank to keep them, or enter new details to replace them."
                                            : ""}
                                    </Typography>
                                </Stack>)}

                            <Stack spacing={2.5}>
                                <TextField
                                    label="User ID"
                                    value={userId}
                                    onChange={(event) => setUserId(event.target.value)}
                                    autoComplete="username"
                                    fullWidth
                                />
                                <TextField
                                    label="Password"
                                    value={password}
                                    onChange={(event) => setPassword(event.target.value)}
                                    type="password"
                                    autoComplete="new-password"
                                    fullWidth
                                />
                            </Stack>

                            <Box sx={{ pt: 0.5 }}>
                                <FormControlLabel
                                    control={<Switch checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />}
                                    label={<Typography sx={{ fontWeight: 600 }}>Enabled</Typography>}
                                />
                            </Box>

                            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                <Button variant="contained" size="large" onClick={() => void save()} disabled={saving} sx={{ minWidth: 150 }}>
                                    {saving ? "Saving…" : "Save changes"}
                                </Button>
                            </Box>
                        </Stack>
                    </Paper>
                )}
            </Stack>
        </Box>
    );
}
